/* StateViewRenderer.js */

"use strict";
const _ = require("lodash");
const ComboBox = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single.ComboBox;
const FileName = require("@gluenetworks/steve_utilities").fileName;

class StateViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("state-view-gl");
        this._rootElem.tabIndex = -1;
        this._rootElem.component = this.model;
        this._defaultViewActive = false;
        this._viewActionsAdded = false;
        this._optionsComboBox = new ComboBox();
        this._optionsLatestLabelElem = document.createElement("options-label-gl");
        this._optionsContainerElem = document.createElement("options-container-gl");
        this.typeContainerElem = document.createElement("icon-container-gl");
        this.typeIconElem = document.createElement("base-icon-gl");
        this.typeIconElem.dataset.icon = "configdrift.camera";
        this.typeContainerElem.appendChild(this.typeIconElem);
        this._optionsContainerElem.appendChild(this.typeContainerElem);
        this._fileNamer = FileName;
        this.obj = {};

        this._commandKeyDown = false;
        this._configViewMain = document.createElement("config-view-main");
        this._tree = document.createElement("tree-view");
        this._tree.style.width =
            (this._model._userDataStorage.getData("treeContainerWidth", true) || 300) + "px";

        this._resizer = document.createElement("div");
        this._resizer.className = "resizer";
        this._resizer.id = "resizer";
        this._documentWidth = document.body.clientWidth;
        this._resizer.style.width = (this._documentWidth * 0.3) / 100 + "px";

        this._configContainerElem = document.createElement("config-container-gl");
        this._configViewMain.appendChild(this._tree);
        this._configViewMain.appendChild(this._resizer);
        this._configContainerRightElem = document.createElement("config-container-right-gl");
        this._configContainerRightElem.appendChild(this._optionsContainerElem);
        this._configContainerRightElem.appendChild(this._configContainerElem);
        this._configViewMain.appendChild(this._configContainerRightElem);
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._optionsComboBox.on(
            "UserValue",
            (this._onComboBoxUserValue = this._onComboBoxUserValue.bind(this))
        );
        this.model.on(
            "DeviceConfigOptionSelected",
            (this._onSelectConfig = this._onSelectConfig.bind(this))
        );
        this.model.on(
            "DeviceConfAdded",
            (this._onDeviceConfAdded = this._onDeviceConfAdded.bind(this))
        );
        this.model.on(
            "DeviceConfRemoved",
            (this._onDeviceConfRemoved = this._onDeviceConfRemoved.bind(this))
        );
        this.model.on(
            "DeviceConfUpdated",
            (this._onDeviceConfUpdated = this._onDeviceConfUpdated.bind(this))
        );
        this._rootElem.addEventListener("keydown", (this._onKeyDown = this._onKeyDown.bind(this)));
        this._rootElem.addEventListener("keyup", (this._onKeyUp = this._onKeyUp.bind(this)));

        this._resizer.addEventListener(
            "mousedown",
            (this._onResizerMousedown = this._onResizerMousedown.bind(this))
        );
        window.addEventListener(
            "resize",
            (this._onWindowResize = () => {
                this._documentWidth = document.body.clientWidth;
                this._tree.style.width =
                    (this._model._userDataStorage.getData("treeContainerWidth", true) || 200) +
                    "px";
            })
        );
    }

    _unregisterListeners() {
        this._optionsComboBox.removeListener("UserValue", this._onComboBoxUserValue);
        this.model.removeListener("DeviceConfigOptionSelected", this._onSelectConfig);
        this.model.removeListener("DeviceConfAdded", this._onDeviceConfAdded);
        this.model.removeListener("DeviceConfRemoved", this._onDeviceConfRemoved);
        this.model.removeListener("DeviceConfUpdated", this._onDeviceConfUpdated);
        this._rootElem.removeEventListener("keydown", this._onKeyDown);
        this._rootElem.removeEventListener("keyup", this._onKeyUp);
        this._resizer.removeEventListener("mousedown", this._onResizerMousedown);
        window.removeEventListener("resize", this._onWindowResize);
    }

    _addViewActions() {
        if (!this._viewActionsAdded) {
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "DownloadConfig",
                handler: (this._onDownloadConfig = this._onDownloadConfig.bind(this)),
            });
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: this._model._viewType === "text" ? "JSON" : "Text",
                name: "switchView",
                position: "left",
                handler: (this._switchViewType = this._switchViewType.bind(this)),
            });
            this._viewActionsAdded = true;
        }
    }

    _removeViewActions() {
        if (this._viewActionsAdded) {
            this.model.gameEditor.clearViewActions();
        }
    }

    _onDownloadConfig() {
        this.model.gameEditor.disableViewAction("DownloadConfig");
        this.model
            .getSelectedDeviceConfig()
            .then((config) => {
                let rawConfig,
                    fileType = "text/plain",
                    fileExt = "txt";
                const rawConfigObj = _.get(config, "configObj", {});
                if (this._model._viewType === "json" && !_.isEmpty(rawConfigObj)) {
                    fileType = "application/json";
                    fileExt = "json";
                    rawConfig = JSON.stringify(rawConfigObj, null, 2);
                } else {
                    rawConfig = _.get(config, "config");
                }
                if (!_.isEmpty(rawConfig)) {
                    const selectedConfig = this.model.getSelectedDeviceConfigOption();
                    // const selectedConfigName = _.get(selectedConfig, "fileName", "");
                    // const specAddendum = selectedConfigName.slice(selectedConfigName.indexOf('('));
                    const deviceName = this.model._device.name;
                    const date = _.get(selectedConfig, "timestamp", new Date());
                    const fileName = this._fileNamer({
                        solution: "DM",
                        general: "DeviceConfig",
                        specific: deviceName,
                        date: date,
                        suffix: "zip",
                    });

                    let fileConfig = {};
                    fileConfig[fileName.slice(0, -4) + "." + fileExt] = {
                        type: fileType,
                        content: rawConfig,
                    };
                    return this.model.io.fileFactory.saveStringsAsZip(fileConfig, fileName);
                }
            })
            .finally(() => {
                this.model.gameEditor.enableViewAction("DownloadConfig");
            });
    }

    _switchViewType() {
        let viewType = this._model._viewType;
        if (viewType === "text") {
            this._model._viewType = "json";
            this._model._userDataStorage.setData("json", "viewType");
        } else {
            this._model._viewType = "text";
            this._model._userDataStorage.setData("text", "viewType");
        }
        this._renderConfigView(this._model._viewType, true);
    }

    _onDeviceConfAdded(/*devConfViewModel, */ selectedId) {
        // TODO - Is there an add option to the combobox?
        this._renderOptions(selectedId);
    }

    _onDeviceConfRemoved(/*devConfViewModel*/) {
        // TODO - Is there a remove option to the combobox?
        this._renderOptions();
    }

    _onDeviceConfUpdated(/*devConfViewModel, */ selectedId) {
        // TODO - Is there an update option to the combobox?
        this._renderOptions(selectedId);
    }

    _onSelectConfig() {
        // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
        // To be refactored with separate views once form layout / style specifications have matured
        if (
            this._model._solutionName !== "device_manager" &&
            this._model._solutionName !== "data_explorer"
        ) {
            let selectedConfig = this.model.selectedDeviceConfigOption;
            this._optionsComboBox.setValue(selectedConfig);

            if (selectedConfig.isStable) {
                this.typeIconElem.dataset.icon = "common.star";
            } else if (_.isUndefined(selectedConfig.wasDifferentAtCaptureTime)) {
                this.typeIconElem.dataset.icon = "devicemanager.discovered";
            } else {
                this.typeIconElem.dataset.icon = "configdrift.camera";
            }

            this._renderConfigView(this._model._viewType, true);
        } else {
            let selectedConfig = this.model.selectedDeviceConfigOption;

            if (_.isUndefined(selectedConfig.wasDifferentAtCaptureTime)) {
                this.typeIconElem.dataset.icon = "devicemanager.discovered";
            } else {
                this.typeIconElem.dataset.icon = "configdrift.camera";
            }

            this._renderConfigView(this._model._viewType, true);
        }
    }

    _renderConfigView(viewType, reusePath = false, path = "") {
        if (this._model._config) {
            this._generateTree();
        }

        this._renderConfig(path, reusePath);
    }

    _renderConfig(path = "", reusePath = false) {
        if (!reusePath) {
            this._model._selectedPath = path || "";
        } else {
            path = this._model._selectedPath || "";
        }

        if (_.get(this._model._config, `${path}`) === undefined) {
            path = "";
        }

        this._setSelection(path);
        let viewType = this._model._viewType;
        const fullConfig = _.get(this._model, "_config", {});
        if (fullConfig) {
            // if (viewType) {
            // let hasJSON = false;
            // if(_.isArray(_.get(fullConfig, path))) {
            //     hasJSON = _.get(fullConfig, path).filter(i => _.isUndefined(i.responseObj)).length === 0;
            // } else {
            //     hasJSON = !_.isUndefined(_.get(fullConfig, `${path}.responseObj`));
            // }

            if (this.isGuid(path)) {
                this._rawConfig = "";
                this._rawConfig = this._getCommandSetConfig(_.get(fullConfig, path), "responseStr");
            } else if (path === "") {
                this._rawConfig = "";
                this._rawConfig = this._getRootConfig(fullConfig, "responseStr");
            } else {
                // if (viewType === "json" && hasJSON) {
                //     if(_.isArray(_.get(fullConfig, `${path}`))) {
                //         rawConfigObj = [];
                //         _.get(fullConfig, path).forEach(i => {
                //             rawConfigObj.push(_.get(i, 'responseObj'));
                //         });
                //     } else {
                //         rawConfigObj = _.get(fullConfig, `${path}.responseObj`);
                //     }

                //     if (!_.isEmpty(rawConfigObj)) {
                //         rawConfig = JSON.stringify(rawConfigObj, null, 2);
                //     }
                // } else {
                const splitPath = path.split(".");
                const commandSetName =
                    _.get(fullConfig, splitPath[0] + ".commandSetName") ||
                    _.get(this._model._gameEditor._commandSetMap, splitPath[0], "Command Set");
                let label = "";
                let valueObj = "";
                if (_.get(fullConfig, `${path}.conceptItemName`, false)) {
                    label = "Concept Item";
                    valueObj = _.get(fullConfig, `${path}.conceptItemName`);
                } else if (_.get(fullConfig, `${path}.restSyncItemName`, false)) {
                    label = "Rest Data Sync Items";
                    valueObj = _.get(fullConfig, `${path}.restSyncItemName`);
                } else if (_.get(fullConfig, `${path}.getUrl`, false)) {
                    label = "Raw HTTP GET requests";
                    valueObj = _.get(fullConfig, `${path}.getUrl`);
                } else if (_.get(fullConfig, `${path}.command`, false)) {
                    label = "CLI Command";
                    valueObj = _.get(fullConfig, `${path}.command`);
                } else {
                    label = "Standard";
                    valueObj = "Standard Configuration";
                }

                this._rawConfig = "";
                if (_.isArray(_.get(fullConfig, `${path}`))) {
                    _.get(fullConfig, `${path}`).forEach((i, idx) => {
                        if (idx > 0) {
                            this._rawConfig += "\n\n";
                        }
                        if (label === "Standard") {
                            this._rawConfig += this._getHeading(true);
                        } else {
                            this._rawConfig += this._getHeading(
                                false,
                                commandSetName,
                                label,
                                valueObj
                            );
                        }

                        this._rawConfig += _.get(i, "responseStr", "");
                    });
                } else {
                    if (label === "Standard") {
                        this._rawConfig += this._getHeading(true);
                    } else {
                        this._rawConfig += this._getHeading(false, commandSetName, label, valueObj);
                    }
                    this._rawConfig += _.get(fullConfig, `${path}.responseStr`);
                }
                // }
                // } else {
                //     if(_.isArray(_.get(fullConfig, `${path}`))) {
                //         _.get(fullConfig, `${path}`).forEach(i => {
                //             rawConfig += '\n' + _.get(i, 'responseStr', '');
                //         })
                //     } else {
                //         rawConfig = _.get(fullConfig, `${path}.responseStr`);
                //     }
                // }
            }
        }

        this._rawConfig = _.trim(this._rawConfig);

        while (this._configContainerElem.hasChildNodes()) {
            this._configContainerElem.removeChild(this._configContainerElem.firstChild);
        }
        if (this._rawConfig && this._rawConfig !== "{}") {
            this._gutterContainerElem = document.createElement("gutter-container-gl");
            this._linesContainerElem = document.createElement("lines-container-gl");
            this._configContainerElem.appendChild(this._gutterContainerElem);
            this._configContainerElem.appendChild(this._linesContainerElem);
            if (this._rawConfig) {
                this._rawConfig = this._rawConfig.replace(/</g, "&lt;");
                _.each(this._rawConfig.split(/\n/), (row, idx) => {
                    row = row.replaceAll(/\r/g, "");
                    if (row.search("apic") >= 0) {
                        // specific to apic to remove the long space between, % and apic
                        row = row.replaceAll(/[ ]*apic/g, " apic");
                    }
                    // LINE NUMBER
                    let lineNumElem = document.createElement("line-number-gl");
                    lineNumElem.innerHTML = idx + 1;
                    // LINE
                    let lineElem = document.createElement("line-gl");
                    lineElem.innerHTML = row;
                    // ROW
                    let rowElem = document.createElement("pre");
                    rowElem.classList.add("config-row-gl");
                    rowElem.appendChild(lineElem);
                    this._gutterContainerElem.appendChild(lineNumElem);
                    this._linesContainerElem.appendChild(rowElem);
                });
            }
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
            this._addViewActions();
            const switchViewActionText = viewType === "json" ? "Text" : "JSON";
            this._model.gameEditor.setViewActionText("switchView", switchViewActionText);
            if (this._model._config.isConfigObj) {
                this.model.gameEditor.showViewAction("switchView");
            } else {
                this.model.gameEditor.hideViewAction("switchView");
            }
        } else {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
            this._removeViewActions();
        }
    }

    _renderIcon() {
        let selectedConfig = this.model.getSelectedDeviceConfigOption();
        if (selectedConfig) {
            let icon = selectedConfig.isStable ? "common.star" : "configdrift.camera";
            this.typeIconElem.dataset.icon = icon;
        } else {
            // TODO Confirm whether we blank out the icon
        }
    }

    _renderOptions(select) {
        let configOptions = this.model.getViewModel();

        // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
        // To be refactored with separate views once form layout / style specifications have matured
        if (
            this._model._solutionName === "device_manager" ||
            this._model._solutionName === "data_explorer"
        ) {
            if (!_.isEmpty(configOptions)) {
                let latestConfig = configOptions[0];

                if (!_.isEmpty(select)) {
                    const selectedItem = configOptions.find((i) => i.value.id === select);
                    if (!_.isEmpty(selectedItem)) {
                        latestConfig = selectedItem;
                    }
                }

                this._optionsLatestLabelElem.innerText = _.get(latestConfig, "label", "");
                this.typeIconElem.dataset.icon = _.get(latestConfig, "value.isStable", false)
                    ? "common.star"
                    : _.get(latestConfig, "value.fromCapture", false)
                    ? "configdrift.camera"
                    : "devicemanager.discovered";
                this.model.setSelectedDeviceConfigOption(latestConfig.value, true);
            }
        } else {
            this._optionsComboBox.setOptions(configOptions);

            if (_.isEmpty(configOptions)) {
                this._optionsComboBox.setDisabled(true);
            } else {
                this._optionsComboBox.setDisabled(false);
            }

            let selectedConfig = this.model.getSelectedDeviceConfigOption();

            if (_.isEmpty(selectedConfig) && configOptions.length === 0) {
                // We got nothing, and nothing to select
            } else if (_.isEmpty(selectedConfig)) {
                // Nothing was selected, but we have some options to choose from
                selectedConfig = configOptions[configOptions.length - 1].value;

                this.model.setSelectedDeviceConfigOption(selectedConfig, true);
                this._optionsComboBox.setValue(selectedConfig);
                this._renderIcon(selectedConfig);
            } else {
                // We have something selected already, so let's use it!
                this._optionsComboBox.setValue(selectedConfig);
                this._renderIcon(selectedConfig);
            }

            if (!_.isEmpty(select)) {
                const selectedItem = configOptions.find((i) => i.value.id === select);

                this._optionsComboBox.setValue(selectedItem.value);
            }
        }
    }

    _onComboBoxUserValue() {
        this.model.setSelectedDeviceConfigOption(this._optionsComboBox.value);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [this._optionsComboBox.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._optionsComboBox = undefined;
                this._optionsContainerElem.remove();
                this._optionsContainerElem = undefined;
                this._configContainerElem.remove();
                this._configContainerElem = undefined;
                this._defaultViewActive = undefined;
                this._commandKeyDown = undefined;
                this._model = undefined;
                this._tree.remove();
                this._tree = undefined;
                this._resizer.remove();
                this._resizer = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn({error: err}, "ConfigViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
            // To be refactored with separate views once form layout / style specifications have matured
            if (
                this._model._solutionName === "device_manager" ||
                this._model._solutionName === "data_explorer"
            ) {
                this._optionsContainerElem.appendChild(this._optionsLatestLabelElem);
            } else {
                this._optionsContainerElem.appendChild(this._optionsComboBox.renderer.rootElement);
            }

            this._rootElem.appendChild(this._configViewMain);
        }
        return this.model.whenModelIsReady().then(() => {
            this._renderOptions();

            const initialPath =
                this._model._userDataStorage.getData("treeSelectedNode", true) || "";

            this._renderConfigView(this._model._viewType, false, initialPath);
            this._rootElem.focus();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    //TODO Private Render Methods here

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * Catch the keydown event
     * @param event {Event}
     * @private
     */
    _onKeyDown(event) {
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = true;
        }
        // Command/CTRL + a/A
        if ((event.ctrlKey === true || this._commandKeyDown === true) && event.keyCode === 65) {
            event.preventDefault();
            if (document.selection) {
                let range = document.body.createTextRange();
                range.moveToElementText(this._linesContainerElem);
                range.select();
            } else if (window.getSelection) {
                let range = document.createRange();
                range.selectNode(this._linesContainerElem);
                window.getSelection().removeAllRanges();
                window.getSelection().addRange(range);
            }
        }
    }

    /**
     * Catch the keyup event
     * @param event {Event}
     * @private
     */
    _onKeyUp(event) {
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = false;
        }
    }

    _onResizerMousedown(e) {
        let treeWidth = this._tree.style.width;
        e = e || window.event;
        let start = 0,
            diff = 0;
        if (e.pageX) {
            start = e.pageX;
        } else if (e.clientX) {
            start = e.clientX;
        }
        document.body.onmousemove = (e) => {
            e = e || window.event;
            let end = 0;
            if (e.pageX) {
                end = e.pageX;
            } else if (e.clientX) {
                end = e.clientX;
            }
            diff = end - start;
            let updatedTreeWidth = this._tree.style.width;
            if (parseInt(updatedTreeWidth, 10) > 100 || diff > 0) {
                this._tree.style.width = parseInt(treeWidth, 10) + diff + "px";
            }
        };
        document.body.onmouseup = () => {
            this._model._userDataStorage.setData(
                parseInt(this._tree.style.width, 10),
                "treeContainerWidth"
            );
            document.body.onmousemove = document.body.onmouseup = null;
        };
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _showDefaultMessageDisplay() {
        this._model.gameEditor
            .showDefaultMessageView({
                message: "No device configuration available",
            })
            .then(() => {
                this._defaultViewActive = true;
            });
    }

    _hideDefaultMessageDisplay() {
        this._model.gameEditor.hideDefaultMessageView().then(() => {
            this._defaultViewActive = false;
        });
    }

    // Tree Code - Start
    _generateTree() {
        const rootKey = "capture";
        this.obj = {
            capture: _.get(this._model, "_config"),
        };
        const rootNode = this.buildTree(this.obj[rootKey], rootKey);
        this._tree.innerHTML = "";
        this._tree.appendChild(rootNode);
        // if (_.get(this.obj, `${rootKey}.basicMode`)) {
        //     this._updateConfiguration("basicMode");
        //     this._setSelection("basicMode");
        // } else {
        //     const firstNodeContainer = this._tree.querySelectorAll(".node-class")[0];
        //     const firstNode = firstNodeContainer.querySelector(".tree-node");
        //     const pathToFirstNode = firstNode.getAttribute("data-path");
        //     if (pathToFirstNode) {
        //         this._updateConfiguration(pathToFirstNode);
        //         this._setSelection(pathToFirstNode);
        //     }
        // }
        // // Expand/Collapse functionality
        const treeNodes = this._tree.querySelectorAll(".tree-node");
        treeNodes.forEach((node) => {
            const toggleButton = node.querySelector(".toggle-button");
            if (toggleButton) {
                toggleButton.addEventListener("click", (e) => {
                    e.stopImmediatePropagation();
                    node.classList.toggle("collapsed");
                });
            }
            node.addEventListener("click", (e) => {
                e.stopImmediatePropagation();

                const path = e.currentTarget.getAttribute("data-path");
                this._model._userDataStorage.setData(path || "", "treeSelectedNode");

                this._renderConfig(path);
                this._setSelection(path);
            });
        });
    }
    createTreeNode(label, className = "", type = undefined, path = "") {
        if (label) {
            const node = document.createElement("div");
            node.className = "tree-node " + className;
            node.setAttribute("data-path", path);
            if (type !== "node") {
                const toggleButton = document.createElement("button");
                toggleButton.className = "toggle-button";
                node.appendChild(toggleButton);
            }
            const span = document.createElement("span");
            span.textContent = label;
            node.appendChild(span);
            return node;
        }
    }

    buildTree(node, key) {
        const diffClass = "node";
        let label = key;
        if (key === "capture") {
            label = "All";
        }
        const treeNode = this.createTreeNode(label, diffClass);
        if (typeof this.obj[key] === "object") {
            const subKeys = new Set([...Object.keys(this.obj[key])]);
            for (const subKey of subKeys) {
                if (subKey === "isObject") {
                    continue;
                }
                let labelVal = subKey;
                if (subKey === "basicMode") {
                    labelVal = "Standard";
                }
                if (this.isGuid(subKey)) {
                    labelVal =
                        _.get(this.obj, `capture.${subKey}.commandSetName`) ||
                        _.get(this._model._gameEditor._commandSetMap, subKey, "Command Set");
                }
                const subDiffClass = "node";
                const subNode = this.createTreeNode(
                    labelVal,
                    `${subDiffClass} ${subKey === "basicMode" ? "basic-node" : ""}`,
                    subKey === "basicMode" ? "node" : undefined,
                    `${subKey}`
                );
                if (Array.isArray(this.obj[key][subKey])) {
                    const subTree = this.buildSubTree(this.obj[key][subKey], subKey, subNode);
                    subNode.appendChild(subTree);
                } else if (typeof this.obj[key][subKey] === "object") {
                    const subSubTree = this.buildSubTree(
                        _.get(this.obj, `${key}.${subKey}`, undefined),
                        subKey,
                        subNode
                    );
                    subNode.appendChild(subSubTree);
                }
                treeNode.appendChild(subNode);
            }
        }
        return treeNode;
    }
    buildSubTree(subObject, subKey, subNodePassed) {
        const subTreeContainer = document.createElement("div");
        if (Array.isArray(subObject)) {
            for (let i = 0; i < Math.max(_.get(subObject, "length", 0)); i++) {
                const item = _.get(subObject, i);
                const diffClass = "node";
                if (item && item.command) {
                    const subNode = this.createTreeNode(item && item.command, diffClass);
                    subTreeContainer.appendChild(subNode);
                }
            }
        } else if (typeof subObject === "object") {
            const subKeys = new Set([..._.keys(subObject)]);
            for (const subSubKey of subKeys) {
                if (!_.isEmpty(_.get(this.obj, `capture.${subKey}.${subSubKey}`))) {
                    let selector = "";
                    if (subSubKey === "cliCommands") {
                        selector = "command";
                    } else if (subSubKey === "conceptItems") {
                        selector = "conceptItemName";
                    } else if (subSubKey === "getUrls") {
                        selector = "getUrl";
                    } else if (subSubKey === "restSyncItems") {
                        selector = "restSyncItemName";
                    }
                    if (
                        !_.isEmpty(_.get(subObject, subSubKey, [])) &&
                        _.isArray(_.get(subObject, subSubKey, []))
                    ) {
                        const subSubTreeContainer = document.createElement("div");
                        subSubTreeContainer.classList.add("node-class");
                        _.get(subObject, subSubKey, []).forEach((item, i) => {
                            const diffClass = "node";
                            const subNode = this.createTreeNode(
                                item[selector],
                                diffClass,
                                "node",
                                `${subKey}.${subSubKey}.${i}`
                            );
                            subSubTreeContainer.appendChild(subNode);
                        });
                        subNodePassed.appendChild(subSubTreeContainer);
                    }
                }
            }
        }
        return subTreeContainer;
    }

    isGuid(str) {
        const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return guidPattern.test(str);
    }

    _updateConfiguration(path = undefined, viewType = "json") {
        const fullConfig = _.get(this._model, "_config", {});
        const confObj = path === "" ? fullConfig : _.get(fullConfig, path);
        // const confObj = path === "" ? this.obj.capture : _.get(this.obj.capture, path);
        if (_.get(confObj, "isObject", false) || _.isEmpty(confObj)) {
            this._model._gameEditor.showViewAction("switchView");
        } else {
            this._model._gameEditor.hideViewAction("switchView");
            this._model._jsonDiff = false;
            this._model._isJsonView = false;
        }
        viewType = "responseStr";
        if (this._model._jsonDiff === true) {
            viewType = "responseObj";
        }
        // if(!(_.isArray(obj1ConfObj) && _.isArray(obj2ConfObj))) {
        if (_.isArray(confObj)) {
            this._model._conf = "";
            if (confObj) {
                confObj.forEach((i) => {
                    this._model._conf += _.get(i, viewType, "") + "\n";
                });
            }
            // } else if (this.isGuid(path)) {
            //     this._getCommandSetConfig(obj1ConfObj, obj2ConfObj, viewType);
            // } else if (path === "") {
            //     this._getRootConfig(obj1ConfObj, obj2ConfObj, viewType);
        } else {
            this._model._conf = _.get(confObj, viewType, "");
        }
        // if (this._model._jsonDiff === true) {
        //     this._model._generateJSONDiffString();
        // } else {
        //     this._model._generateDiffString();
        // }

        return this._model._conf;
    }

    _setSelection(path) {
        this._selectedPath = path;
        const treeNodes = this._tree.querySelectorAll(".tree-node");
        treeNodes.forEach((node) => {
            node.classList.remove("selected");
        });
        const selectedNode = this._tree.querySelector(".tree-node[data-path='" + path + "']");
        if (selectedNode) {
            selectedNode.classList.add("selected");
        }
    }

    _getCommandSetConfig(obj1, viewType) {
        let _conf = "";
        if (_.isObject(obj1)) {
            Object.entries(obj1 || {}).forEach(([lab, val]) => {
                if (!_.isEmpty(val)) {
                    let label = "";
                    let valueObj = "";
                    if (lab === "cliCommands") {
                        label = "CLI Command";
                        valueObj = "command";
                    } else if (lab === "conceptItems") {
                        label = "Concept Item";
                        valueObj = "conceptItemName";
                    } else if (lab === "getUrls") {
                        label = "Raw HTTP GET request";
                        valueObj = "getUrl";
                    } else if (lab === "restSyncItems") {
                        label = "Rest Data Sync Item";
                        valueObj = "restSyncItemName";
                    }

                    if (_.isArray(val)) {
                        val.forEach((item) => {
                            const commandSetName =
                                _.get(obj1, "commandSetName", false) ||
                                _.get(this._model._gameEditor._commandSetMap, obj1, "Command Set");

                            _conf += this._getHeading(false, commandSetName, label, item[valueObj]);

                            _conf += _.get(item, viewType, "");
                        });
                    }
                }
            });
        }
        return _.trim(_conf);
    }
    _getRootConfig(obj1, viewType) {
        let _conf = "";
        // Config for Object 1
        if (obj1 && _.isObject(obj1)) {
            if (_.get(obj1, "basicMode")) {
                // Standard Config
                _conf += this._getHeading(true);
                obj1.basicMode.forEach((i) => {
                    _conf += _.get(i, viewType);
                });
            }
            Object.entries(obj1).forEach(([labCs, valCs]) => {
                if (labCs !== "basicMode") {
                    if (this.isGuid(labCs)) {
                        //Command Set Name

                        Object.entries(valCs || {}).forEach(([lab, val]) => {
                            if (!_.isEmpty(val)) {
                                let label = "";
                                let valueObj = "";
                                if (lab === "cliCommands") {
                                    label = "CLI Command";
                                    valueObj = "command";
                                } else if (lab === "conceptItems") {
                                    label = "Concept Item";
                                    valueObj = "conceptItemName";
                                } else if (lab === "getUrls") {
                                    label = "Raw HTTP GET request";
                                    valueObj = "getUrl";
                                } else if (lab === "restSyncItems") {
                                    label = "Rest Data Sync Item";
                                    valueObj = "restSyncItemName";
                                }

                                if (_.isArray(val)) {
                                    val.forEach((item) => {
                                        const commandSetName =
                                            _.get(valCs, "commandSetName", false) ||
                                            _.get(
                                                this._model._gameEditor._commandSetMap,
                                                labCs,
                                                "Command Set"
                                            );
                                        _conf += this._getHeading(
                                            false,
                                            commandSetName,
                                            label,
                                            item[valueObj]
                                        );

                                        _conf += "\n";
                                        _conf += _.get(item, viewType, "");
                                    });
                                }
                            }
                        });
                    }
                }
            });
        }

        return _.trim(_conf);
    }

    _getHeading(standard, commandSetName, label, valueObj) {
        let str = "";
        if (standard) {
            str +=
                "**************************************************************************************\n";
            str += "Standard Configuration";
            str +=
                "\n**************************************************************************************\n\n";
        } else {
            str += "\n\n\n";

            str +=
                "**************************************************************************************\n";
            str += "Command Set : " + commandSetName;
            str += "\n" + label + " : " + valueObj;
            str +=
                "\n**************************************************************************************\n\n";
        }

        return str;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = StateViewRenderer;
